import {
  Search,
  Minus,
  Plus,
  Calendar,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CardContent } from "@/components/ui/card";
import { FilterButton } from "@/components/search-filter-bar/components/filter-button";
import { SortButton } from "@/components/search-filter-bar/components/sort-button";
import { DatePickerV2 } from "@/components/fields/date-picker";
import dayjs from "dayjs";
import { cn } from "@/lib/utils";
import type {
  FilterableAttribute,
  SortableAttribute,
  DynamicFilter,
  DynamicSort
} from "@/components/search-filter-bar/types";
import type { ViewMode } from "@/hooks/services/pps/handlers/schedule/job-schedule-types";
import { ZOOM_MIN, ZOOM_MAX } from "../utils";

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface ScheduleCalendarHeaderProps {
  title: string;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  filterableAttributes: FilterableAttribute[];
  dynamicFilters: DynamicFilter[];
  onAddFilter: (attribute: FilterableAttribute) => void;
  onUpdateFilter: (index: number, filter: DynamicFilter) => void;
  onRemoveFilter: (index: number) => void;
  onClearFilters: () => void;
  sortableAttributes: SortableAttribute[];
  dynamicSorts: DynamicSort[];
  onAddSort: (attribute: SortableAttribute) => void;
  onUpdateSort: (index: number, sort: DynamicSort) => void;
  onRemoveSort: (index: number) => void;
  onClearSorts: () => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  zoomLevel: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  headerAction?: ReactNode;
}

/** Derived from viewMode: "daily" -> timeline tab; "weekly"|"monthly" -> calendar tab */
type ActiveTab = "timeline" | "calendar";

function getActiveTab(viewMode: ViewMode): ActiveTab {
  return viewMode === "daily" ? "timeline" : "calendar";
}

/**
 * Given a picked range, cap it to at most 7 days starting from `from`.
 * If only `from` is set, snaps to Mon–Sun of that week.
 */
function snapWeekRange(from: Date, to: Date | null | undefined): DateRange {
  if (to) {
    const start = dayjs(from).startOf("day");
    const end = dayjs(to).startOf("day");
    const spanDays = end.diff(start, "day") + 1;
    const cappedEnd = spanDays > 7 ? start.add(6, "day").toDate() : to;
    return { from, to: cappedEnd };
  }
  // Snap to Mon–Sun
  const d = dayjs(from);
  const diffToMonday = d.day() === 0 ? -6 : 1 - d.day();
  const weekStart = d.add(diffToMonday, "day").startOf("day");
  return { from: weekStart.toDate(), to: weekStart.add(6, "day").toDate() };
}

export default function ScheduleCalendarHeader({
  title,
  dateRange,
  onDateRangeChange,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  filterableAttributes,
  dynamicFilters,
  onAddFilter,
  onUpdateFilter,
  onRemoveFilter,
  onClearFilters,
  sortableAttributes,
  dynamicSorts,
  onAddSort,
  onUpdateSort,
  onRemoveSort,
  onClearSorts,
  viewMode,
  onViewModeChange,
  zoomLevel,
  onZoomIn,
  onZoomOut,
  headerAction
}: ScheduleCalendarHeaderProps) {
  const activeTab = getActiveTab(viewMode);

  const handleTabChange = (tab: ActiveTab) => {
    if (tab === "timeline") {
      onViewModeChange("daily");
    } else {
      // Default to weekly when switching to Calendar tab
      onViewModeChange("weekly");
    }
  };

  const handleToday = () => {
    const today = new Date();
    if (viewMode === "daily") {
      onDateRangeChange({ from: today, to: today });
    } else if (viewMode === "weekly") {
      onDateRangeChange(snapWeekRange(today, null));
    } else {
      onDateRangeChange({
        from: dayjs(today).startOf("month").toDate(),
        to: dayjs(today).endOf("month").toDate()
      });
    }
  };

  const handlePrev = () => {
    if (!dateRange.from) return;
    if (viewMode === "daily") {
      const d = dayjs(dateRange.from).subtract(1, "day").toDate();
      onDateRangeChange({ from: d, to: d });
    } else if (viewMode === "weekly") {
      const span = dateRange.to
        ? dayjs(dateRange.to).diff(dayjs(dateRange.from), "day") + 1
        : 7;
      const start = dayjs(dateRange.from).subtract(span, "day").toDate();
      onDateRangeChange(
        snapWeekRange(
          start,
          dayjs(start)
            .add(span - 1, "day")
            .toDate()
        )
      );
    } else {
      const start = dayjs(dateRange.from)
        .subtract(1, "month")
        .startOf("month")
        .toDate();
      onDateRangeChange({
        from: start,
        to: dayjs(start).endOf("month").toDate()
      });
    }
  };

  const handleNext = () => {
    if (!dateRange.from) return;
    if (viewMode === "daily") {
      const d = dayjs(dateRange.from).add(1, "day").toDate();
      onDateRangeChange({ from: d, to: d });
    } else if (viewMode === "weekly") {
      const span = dateRange.to
        ? dayjs(dateRange.to).diff(dayjs(dateRange.from), "day") + 1
        : 7;
      const start = dayjs(dateRange.from).add(span, "day").toDate();
      onDateRangeChange(
        snapWeekRange(
          start,
          dayjs(start)
            .add(span - 1, "day")
            .toDate()
        )
      );
    } else {
      const start = dayjs(dateRange.from)
        .add(1, "month")
        .startOf("month")
        .toDate();
      onDateRangeChange({
        from: start,
        to: dayjs(start).endOf("month").toDate()
      });
    }
  };

  return (
    <CardContent className="aero:p-0">
      {/* ── Title bar — Figma 235-106137 / 235-106140 ─────────────────── */}
      <div className="aero:flex aero:items-center aero:justify-between aero:px-5 aero:py-5 aero:border-b aero:border-neutral-200 aero:bg-white">
        <div className="aero:flex aero:items-center aero:gap-2.5">
          <div className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full aero:flex-shrink-0" />
          <h1 className="aero:text-xl aero:font-bold aero:text-brand-primary-600 aero:leading-7">
            {title}
          </h1>
        </div>
        {headerAction && (
          <div className="aero:flex aero:items-center">{headerAction}</div>
        )}
      </div>

      {/* Row 1: Calendar / Timeline tabs  +  Search / Filter / Sort */}
      <div className="aero:relative aero:flex aero:items-center aero:justify-between aero:px-10 aero:py-3 aero:border-b aero:border-gray-200">
        {/* Tab switcher */}
        <div className="aero:flex aero:items-center aero:gap-8">
          {(["calendar", "timeline"] as ActiveTab[]).map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => handleTabChange(tab)}
                className={cn(
                  "aero:relative aero:flex aero:items-center aero:gap-2 aero:py-1 aero:text-base aero:font-bold aero:transition-colors",
                  isActive
                    ? "aero:text-brand-primary-600"
                    : "aero:text-neutral-400 aero:hover:text-neutral-600"
                )}
              >
                {tab === "timeline" ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M2.5 3.75H13.75C14.4404 3.75 15 4.30964 15 5V6.25C15 6.94036 14.4404 7.5 13.75 7.5H2.5C1.80964 7.5 1.25 6.94036 1.25 6.25V5C1.25 4.30964 1.80964 3.75 2.5 3.75ZM0 5C0 3.61929 1.11929 2.5 2.5 2.5H13.75C15.1307 2.5 16.25 3.61929 16.25 5V6.25C16.25 7.63071 15.1307 8.75 13.75 8.75H2.5C1.11929 8.75 0 7.63071 0 6.25V5ZM6.25 12.5H17.5C18.1904 12.5 18.75 13.0596 18.75 13.75V15C18.75 15.6904 18.1904 16.25 17.5 16.25H6.25C5.55964 16.25 5 15.6904 5 15V13.75C5 13.0596 5.55964 12.5 6.25 12.5ZM3.75 13.75C3.75 12.3693 4.86929 11.25 6.25 11.25H17.5C18.8807 11.25 20 12.3693 20 13.75V15C20 16.3807 18.8807 17.5 17.5 17.5H6.25C4.86929 17.5 3.75 16.3807 3.75 15V13.75Z"
                      fill={isActive ? "#416D6D" : "#A1A1A1"}
                    />
                  </svg>
                ) : (
                  <Calendar className="aero:h-5 aero:w-5" />
                )}
                {tab === "timeline" ? "Timeline" : "Calendar"}
                {/* Active underline pill — overlaps the border-b */}
                {isActive && (
                  <span className="aero:absolute aero:bottom-[-18.5px] aero:left-0 aero:right-0 aero:h-[4px] aero:bg-brand-primary-600 aero:rounded-full aero:z-10" />
                )}
              </button>
            );
          })}
        </div>

        {/* Search + Filter + Sort */}
        <div className="aero:flex aero:items-center aero:gap-2">
          <div className="aero:relative">
            <Search className="aero:absolute aero:left-3 aero:top-1/2 aero:-translate-y-1/2 aero:h-4 aero:w-4 aero:text-muted-foreground" />
            <Input
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className="aero:pl-9 aero:w-72"
            />
          </div>
          <FilterButton
            filterableAttributes={filterableAttributes}
            dynamicFilters={dynamicFilters}
            onAddFilter={onAddFilter as any}
            onUpdateFilter={onUpdateFilter as any}
            onRemoveFilter={onRemoveFilter}
            onClearFilters={onClearFilters}
            className="aero:mt-0"
          />
          <SortButton
            sortableAttributes={sortableAttributes}
            dynamicSorts={dynamicSorts}
            onAddSort={onAddSort as any}
            onUpdateSort={onUpdateSort as any}
            onRemoveSort={onRemoveSort}
            onClearSorts={onClearSorts}
            className="aero:mt-0"
          />
        </div>
      </div>
      {/* Row 2: Date navigation  +  Day/Week/Month toggle + Zoom */}
      <div className="aero:flex aero:items-center aero:justify-between aero:px-10 aero:py-2.5">
        {/* Left: date navigation controls — layout varies by viewMode */}
        <div className="aero:flex aero:items-center aero:gap-2.5">
          {viewMode === "monthly" ? (
            /* Monthly: "Month" label badge + month picker (Figma 193-111377) */
            <>
              {/* Static "Month" period-type badge */}
              <div className="aero:flex aero:items-center aero:h-10 aero:px-2.5 aero:bg-white aero:border aero:border-gray-300 aero:rounded">
                <span className="aero:text-base aero:text-neutral-900 aero:whitespace-nowrap">
                  Month
                </span>
              </div>
              {/* Month picker input — 186×40px Figma 206-137979 */}
              <DatePickerV2
                key={`monthly-${dateRange.from?.toISOString()}`}
                value={dateRange.from}
                onChange={(date) => {
                  if (date) {
                    onDateRangeChange({
                      from: new Date(date.getFullYear(), date.getMonth(), 1),
                      to: new Date(date.getFullYear(), date.getMonth() + 1, 0)
                    });
                  }
                }}
                monthOnly
                placeholder="Select month"
                className="aero:w-[186px]"
                triggerClassName="aero:h-10 aero:border-gray-300 aero:rounded aero:shadow-none"
              />
            </>
          ) : (
            <>
              {viewMode === "daily" ? (
                <Button design="outline" size="sm" onClick={handleToday}>
                  Today
                </Button>
              ) : (
                /* Prev / Today / Next navigation pill for weekly */
                <div className="aero:flex aero:items-center aero:border aero:border-gray-300 aero:rounded-lg aero:overflow-hidden">
                  <button
                    type="button"
                    onClick={handlePrev}
                    className="aero:flex aero:items-center aero:justify-center aero:px-2 aero:py-1.5 aero:hover:bg-gray-50 aero:border-r aero:border-gray-300 aero:transition-colors"
                    title="Previous"
                  >
                    <ChevronLeft className="aero:h-4 aero:w-4 aero:text-gray-600" />
                  </button>
                  <button
                    type="button"
                    onClick={handleToday}
                    className="aero:px-3 aero:py-1.5 aero:text-sm aero:font-medium aero:text-gray-700 aero:hover:bg-gray-50 aero:border-r aero:border-gray-300 aero:transition-colors"
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="aero:flex aero:items-center aero:justify-center aero:px-2 aero:py-1.5 aero:hover:bg-gray-50 aero:transition-colors"
                    title="Next"
                  >
                    <ChevronRight className="aero:h-4 aero:w-4 aero:text-gray-600" />
                  </button>
                </div>
              )}

              {viewMode === "daily" && (
                <DatePickerV2
                  key={`daily-${dateRange.from?.toISOString()}`}
                  value={dateRange.from}
                  onChange={(date) => {
                    if (date) onDateRangeChange({ from: date, to: date });
                  }}
                  placeholder="Select date"
                  showTime={false}
                  className="aero:w-[180px]"
                />
              )}
              {viewMode === "weekly" && (
                <div className="aero:flex aero:items-center aero:gap-2">
                  <DatePickerV2
                    key={`weekly-${dateRange.from?.toISOString()}`}
                    range
                    rangeValue={dateRange}
                    onRangeChange={(range) => {
                      if (range?.from) {
                        onDateRangeChange(snapWeekRange(range.from, range.to));
                      }
                    }}
                    placeholder="Pick start – end (max 7 days)"
                    showTime={false}
                    className="aero:w-[220px]"
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Right: Day / Week / Month segmented pill + Zoom */}
        <div className="aero:flex aero:items-center aero:gap-3">
          {/* Segmented pill — same options always shown; clicking cross-tab redirects */}
          <div className="aero:flex aero:items-center aero:bg-white aero:border aero:border-gray-300 aero:rounded-lg aero:p-1 aero:gap-0">
            {(
              [
                { label: "Day", mode: "daily" as ViewMode },
                { label: "Week", mode: "weekly" as ViewMode },
                { label: "Month", mode: "monthly" as ViewMode }
              ] as { label: string; mode: ViewMode }[]
            ).map(({ label, mode }) => {
              const isActive = viewMode === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => {
                    onViewModeChange(mode);
                    // Adjust date range to match the new view so the calendar
                    // always shows a valid range instead of carrying over a
                    // single-day selection from daily view.
                    const anchor = dateRange.from ?? new Date();
                    if (mode === "weekly") {
                      onDateRangeChange(snapWeekRange(anchor, null));
                    } else if (mode === "monthly") {
                      onDateRangeChange({
                        from: dayjs(anchor).startOf("month").toDate(),
                        to: dayjs(anchor).endOf("month").toDate()
                      });
                    } else if (mode === "daily") {
                      // Snap to the start of the current range (or today)
                      onDateRangeChange({ from: anchor, to: anchor });
                    }
                  }}
                  className={cn(
                    "aero:flex-1 aero:px-6 aero:py-1 aero:text-sm aero:rounded-lg aero:transition-colors aero:whitespace-nowrap",
                    isActive
                      ? "aero:bg-brand-primary-50 aero:border aero:border-brand-primary-700 aero:text-brand-primary-700 aero:font-bold"
                      : "aero:text-gray-900 aero:font-normal aero:hover:bg-gray-50"
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Zoom control — Figma: border-brand-primary-700 rounded-lg, minus / value / plus */}
          <div className="aero:flex aero:items-center aero:gap-2 aero:border aero:border-brand-primary-700 aero:rounded-lg aero:px-3 aero:py-2 aero:bg-white">
            <button
              type="button"
              onClick={onZoomOut}
              disabled={zoomLevel <= ZOOM_MIN}
              className="aero:flex aero:items-center aero:justify-center aero:w-4 aero:h-4 aero:disabled:opacity-40"
              title="Zoom out"
            >
              <Minus className="aero:h-4 aero:w-4 aero:text-brand-primary-700" />
            </button>
            <span className="aero:text-sm aero:font-bold aero:text-brand-primary-700 aero:w-9 aero:text-center aero:tabular-nums">
              {zoomLevel}%
            </span>
            <button
              type="button"
              onClick={onZoomIn}
              disabled={zoomLevel >= ZOOM_MAX}
              className="aero:flex aero:items-center aero:justify-center aero:w-4 aero:h-4 aero:disabled:opacity-40"
              title="Zoom in"
            >
              <Plus className="aero:h-4 aero:w-4 aero:text-brand-primary-700" />
            </button>
          </div>
        </div>
      </div>
    </CardContent>
  );
}
